import type { Measurement } from '@cognite/apm-client';

import type { MeasurementResponseType } from '../template/cdm-types';
import { cdmGraphQLTimeseriesResponseToTimeseries } from '../timeseries/utils/cdm-timeseries-convert-utils';

import { sortOrderAscending } from './sort-order-ascending';

const mapCdmMeasurementToMeasurement = (
  cdmMeasurement: MeasurementResponseType
): Measurement => ({
  ...cdmMeasurement,
  timeseries: cdmMeasurement.timeseries
    ? cdmGraphQLTimeseriesResponseToTimeseries(cdmMeasurement.timeseries)
    : undefined,
});

/**
 * Transforms an array of CDM measurement responses to Measurement objects,
 * converting timeseries and sorting by order in ascending order.
 *
 * @param cdmMeasurements - Array of CDM measurement responses from a GraphQL call.
 * @returns Sorted array of Measurement objects.
 */
export const mapCdmMeasurementsToMeasurements = (
  cdmMeasurements: MeasurementResponseType[]
): Measurement[] =>
  cdmMeasurements.map(mapCdmMeasurementToMeasurement).sort(sortOrderAscending);

const normalizeTimeseriesUnit = (
  ts: Measurement['timeseries']
): Measurement['timeseries'] => {
  if (!ts || ts.unit != null) {
    return ts;
  }
  if ('sourceUnit' in ts && typeof ts.sourceUnit === 'string') {
    return { ...ts, unit: ts.sourceUnit };
  }
  return ts;
};

/**
 * Normalizes measurements from the DMS flat-array path (e.g. checklist detail),
 * mapping CDM `sourceUnit` onto SDK `unit` when needed, then sorting by order.
 */
export const mapDmsMeasurementsToMeasurements = (
  measurements: Measurement[]
): Measurement[] =>
  measurements
    .map((m) => ({ ...m, timeseries: normalizeTimeseriesUnit(m.timeseries) }))
    .sort(sortOrderAscending);
